"use client"

import { Fragment, useMemo, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { IconChevronRight } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useFormatter } from "@/hooks/use-formatter"
import { usePermissions } from "@/hooks/use-permissions"
import {
  getAffiliatorIntakes,
  markAffiliatorIntakeOpened,
  type AffiliatorIntakeList,
  type AffiliatorIntakeRow,
} from "@/lib/affiliator-intake-api"
import { clientRecordHref } from "@/lib/client-record-ux"
import { hasFlag } from "@/lib/feature-flags"
import { matchesFlightWindow } from "@/lib/flight-date-match"
import { FlightDateFilter, type FlightDateValue } from "@/components/filters/flight-date-filter"
import { useAuthStore } from "@/stores/auth-store"

/** The group key of a patient whose partner account has since been deleted. */
const NO_PARTNER = "none"

/** New registration, or a partner-added trip nobody has opened since (ArtDent 261023). */
function isUnopened(row: AffiliatorIntakeRow): boolean {
  return row.opened_at === null || (!!row.trip && row.trip.opened_at === null)
}

function partnerKeyOf(row: AffiliatorIntakeRow): string {
  return row.affiliator ? String(row.affiliator.id) : NO_PARTNER
}

interface PartnerGroup {
  key: string
  /** Null for the patients whose partner account is gone. */
  name: string | null
  rows: AffiliatorIntakeRow[]
  /** Rows nobody on the staff has opened yet. */
  unopened: number
}

/**
 * The rows by the partner who registered them. The server sends newest first,
 * so a partner's first appearance orders the groups by their latest
 * registration, and the rows inside each group stay newest first.
 */
function groupByPartner(rows: AffiliatorIntakeRow[]): PartnerGroup[] {
  const groups = new Map<string, PartnerGroup>()
  for (const row of rows) {
    const key = partnerKeyOf(row)
    const group = groups.get(key) ?? {
      key,
      name: row.affiliator?.full_name ?? null,
      rows: [],
      unopened: 0,
    }
    group.rows.push(row)
    if (isUnopened(row)) group.unopened += 1
    groups.set(key, group)
  }
  return [...groups.values()]
}

/**
 * "New from partners" (ArtDent 261007) — the patients a partner registered
 * themselves in the last 30 days, with the partner's name, at the top of the
 * admin and coordinator dashboards.
 *
 * The New pill is SHARED, not per user (client decision): the first staff
 * member to open the record clears it for everyone, and the row then says who
 * that was. The record page reports the open, so the patient may be opened
 * from anywhere — this panel, the bell, the Clients list, the phone.
 *
 * ArtDent 261015: a TABLE in the desk board's idiom — one subhead row per
 * partner, then their patients (name and New pill, when they were registered,
 * who opened them) — with a partner filter and an All patients / New only
 * filter in the header. Both run on the rows already loaded: a month of
 * partner registrations sits well inside the panel's cap, so the counts on
 * the filters are the counts on screen. A row opens the patient.
 *
 * Self-gating like TravelDeskCard: on a clinic without the flag, or for a user
 * without the whole-clinic patient grant, it renders nothing and asks nothing.
 */
export function AffiliatorIntakeCard() {
  const t = useTranslations("dashboard.affiliatorIntake")
  const format = useFormatter()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isLoaded, can } = usePermissions()
  const user = useAuthStore((s) => s.user)
  const flagOn = hasFlag(user?.feature_flags, "affiliator_intake_panel_enabled")
  const clientRecordUx = hasFlag(user?.feature_flags, "client_record_ux_enabled")
  // ArtDent 261016 — the Phase 1 / Phase 2 the partner picked on registration.
  const phaseShown = hasFlag(user?.feature_flags, "affiliator_registration_phase_enabled")
  const columnCount = phaseShown ? 5 : 4
  const allowed = isLoaded && flagOn && can("leads.view.all")

  const queryKey = ["affiliator-intakes", user?.id]
  const query = useQuery({
    queryKey,
    queryFn: getAffiliatorIntakes,
    enabled: allowed,
    // The pill must be gone when the user comes back from the record, and a
    // desk that leaves the dashboard open should see new registrations land.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  })

  const [partner, setPartner] = useState<string>("all")
  const [status, setStatus] = useState<"all" | "new">("all")
  const [phase, setPhase] = useState<"all" | "1" | "2">("all")
  // A clinic that never asks has no phases to filter by.
  const phaseValue = phaseShown ? phase : "all"
  // ArtDent 261022 — the patients flying in or home in a window. The control
  // self-gates on its flag; with it off the window stays empty and keeps all.
  const [flight, setFlight] = useState<FlightDateValue>({})

  const rows = query.data?.data
  const partners = useMemo(() => groupByPartner(rows ?? []), [rows])
  const phaseCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0 }
    for (const row of rows ?? []) if (row.phase) counts[row.phase] += 1
    return counts
  }, [rows])
  // A partner who drops out of the window (the list refreshes every minute)
  // takes the filter back to everyone rather than leaving an empty panel.
  const partnerValue = partners.some((p) => p.key === partner) ? partner : "all"
  const groups = useMemo(() => {
    const picked =
      partnerValue === "all" ? partners : partners.filter((p) => p.key === partnerValue)
    const flightWindow = Boolean(flight.from || flight.to)
    if (status === "all" && phaseValue === "all" && !flightWindow) return picked
    const keep = (r: AffiliatorIntakeRow) =>
      (status === "all" || isUnopened(r)) &&
      (phaseValue === "all" || String(r.phase) === phaseValue) &&
      matchesFlightWindow(r.flights, flight)
    // The subhead's new count follows the rows left on screen.
    return picked
      .map((p) => {
        const kept = p.rows.filter(keep)
        return { ...p, rows: kept, unopened: kept.filter(isUnopened).length }
      })
      .filter((p) => p.rows.length > 0)
  }, [partners, partnerValue, status, phaseValue, flight])

  if (!allowed) return null

  // The record page reports the open too; doing it here as well clears the
  // pill at once, and covers a row opened in a new tab.
  const markOpened = (leadId: string) => {
    queryClient.setQueryData<AffiliatorIntakeList>(queryKey, (current) => {
      if (!current) return current
      let cleared = 0
      const data = current.data.map((row) => {
        if (row.lead_id !== leadId || !isUnopened(row)) return row
        cleared += 1
        const stamp = new Date().toISOString()
        const opener = user ? { id: Number(user.id), full_name: user.full_name } : null
        return {
          ...row,
          opened_at: row.opened_at ?? stamp,
          opened_by: row.opened_at === null ? opener : row.opened_by,
          trip: row.trip && row.trip.opened_at === null ? { ...row.trip, opened_at: stamp, opened_by: opener } : row.trip,
        }
      })
      return { data, meta: { ...current.meta, unopened: current.meta.unopened - cleared } }
    })
    markAffiliatorIntakeOpened(leadId).catch(() => undefined)
  }

  const open = (row: AffiliatorIntakeRow) => {
    if (isUnopened(row)) markOpened(row.lead_id)
    router.push(clientRecordHref(row.lead_id, clientRecordUx))
  }

  const when = (value: string) =>
    format.dateTime(new Date(value), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })

  /** "12 Oct → 19 Oct" from `yyyy-MM-dd` dates (parsed as local, not UTC). */
  const tripDates = (arrival: string, departure: string | null) => {
    const day = (value: string) => format.dateTime(new Date(`${value}T00:00:00`), { day: "numeric", month: "short" })
    return departure ? `${day(arrival)} → ${day(departure)}` : day(arrival)
  }

  const unopened = query.data?.meta.unopened ?? 0
  const days = query.data?.meta.days ?? 30
  const total = rows?.length ?? 0
  const shown = groups.reduce((n, group) => n + group.rows.length, 0)

  return (
    <ApolloPanel
      eyebrow={t("eyebrow")}
      // The filters wrap under the eyebrow when the panel is narrow; `grow`
      // keeps them right-aligned either way.
      headerClassName="flex-wrap items-center"
      actionsClassName="min-w-0 shrink grow"
      actions={
        <div className="flex grow flex-wrap items-center justify-end gap-2">
          {unopened > 0 && <Badge>{t("newCount", { count: unopened })}</Badge>}
          {/* The filters exist only once there is something to filter. */}
          {total > 0 && (
            <>
              <Select value={partnerValue} onValueChange={setPartner}>
                <SelectTrigger size="sm" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allPartners")}</SelectItem>
                  {partners.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      <span className="flex items-center gap-2">
                        {p.name ?? t("noPartner")}
                        <span className="tabular-nums text-muted-foreground">{p.rows.length}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={(v) => setStatus(v as "all" | "new")}>
                <SelectTrigger size="sm" className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allPatients")}</SelectItem>
                  <SelectItem value="new">
                    <span className="flex items-center gap-2">
                      {t("newOnly")}
                      <span className="tabular-nums text-muted-foreground">{unopened}</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {phaseShown && (
                <Select value={phase} onValueChange={(v) => setPhase(v as "all" | "1" | "2")}>
                  <SelectTrigger size="sm" className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allPhases")}</SelectItem>
                    {([1, 2] as const).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        <span className="flex items-center gap-2">
                          {t("phase", { phase: n })}
                          <span className="tabular-nums text-muted-foreground">{phaseCounts[n]}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FlightDateFilter value={flight} onChange={setFlight} />
            </>
          )}
        </div>
      }
      bodyClassName="p-0 pt-2"
    >
      {query.isPending ? (
        <div className="space-y-2 px-4 py-2 md:px-5" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2 md:px-5">
          <p className="text-sm text-muted-foreground">{t("loadFailed")}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t("retry")}
          </Button>
        </div>
      ) : total === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground md:px-5">{t("empty", { days })}</p>
      ) : shown === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground md:px-5">{t("emptyFiltered")}</p>
      ) : (
        <div className="scrollbar-thin max-h-[320px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-surface">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">{t("colPatient")}</TableHead>
                {phaseShown && <TableHead className="whitespace-nowrap">{t("colPhase")}</TableHead>}
                <TableHead className="whitespace-nowrap text-right">{t("colRegistered")}</TableHead>
                <TableHead className="whitespace-nowrap">{t("colOpenedBy")}</TableHead>
                <TableHead className="w-8 pr-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => {
                const name = group.name ?? t("noPartner")
                return (
                  <Fragment key={group.key}>
                    {/* The partner's subhead: who, how many, how many new. Under
                        "New only" every row is new, so the count that would
                        repeat it is left off. */}
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={columnCount} className="bg-muted/40 py-1.5 pl-4 md:pl-5">
                        <span className="flex items-center justify-between gap-3">
                          <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {name}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {t("groupCount", { count: group.rows.length })}
                            {status === "all" && group.unopened > 0
                              ? ` · ${t("newCount", { count: group.unopened })}`
                              : null}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                    {group.rows.map((row) => {
                      const isNew = isUnopened(row)
                      const tripNew = !!row.trip && row.trip.opened_at === null
                      const href = clientRecordHref(row.lead_id, clientRecordUx)
                      return (
                        <TableRow
                          key={row.lead_id}
                          className="cursor-pointer"
                          onClick={() => open(row)}
                        >
                          <TableCell className="py-2 pl-4 md:pl-5">
                            <span className="flex items-center gap-2">
                              <Link
                                href={href}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (isNew) markOpened(row.lead_id)
                                }}
                                className={`truncate text-sm hover:text-primary ${isNew ? "font-semibold" : "font-medium"}`}
                              >
                                {row.patient_name}
                              </Link>
                              {isNew && <Badge>{t("new")}</Badge>}
                              {/* ArtDent 261023 — the partner added a further trip. */}
                              {row.trip && (
                                <Badge
                                  variant="outline"
                                  className={tripNew ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400" : "text-muted-foreground"}
                                >
                                  {row.trip.number ? t("trip", { number: row.trip.number }) : t("tripUnnumbered")}
                                  {row.trip.arrival_date && (
                                    <span className="ml-1 font-normal tabular-nums">
                                      {tripDates(row.trip.arrival_date, row.trip.departure_date)}
                                    </span>
                                  )}
                                </Badge>
                              )}
                            </span>
                          </TableCell>
                          {phaseShown && (
                            <TableCell className="whitespace-nowrap py-2">
                              {/* Registrations from before the question was asked carry none. */}
                              {row.phase ? (
                                <Badge variant="outline">{t("phase", { phase: row.phase })}</Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="whitespace-nowrap py-2 text-right text-xs tabular-nums text-muted-foreground">
                            {/* A partner-added trip is the newer event: its time, not the registration's. */}
                            {when(tripNew && row.trip ? row.trip.added_at : row.registered_at)}
                          </TableCell>
                          <TableCell className="max-w-[12rem] truncate py-2 text-xs text-muted-foreground">
                            {(row.trip?.opened_by ?? row.opened_by)?.full_name ?? (isNew ? "" : "—")}
                          </TableCell>
                          <TableCell className="py-2 pr-3 text-right">
                            <IconChevronRight className="size-4 text-muted-foreground" aria-hidden />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </ApolloPanel>
  )
}
